import { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  MessageSquare, 
  Users, 
  Calendar,
  Shield,
  Zap,
  Calculator,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

// Feature options with hours
const FEATURES = [
  { label: 'User Accounts & Authentication', value: 'auth', hours: 80 },
  { label: 'Roles & Permissions', value: 'roles', hours: 70 },
  { label: 'Dashboards', value: 'dashboard', hours: 120 },
  { label: 'Reports', value: 'reports', hours: 140 },
  { label: 'API Integrations', value: 'api', hours: 160 },
  { label: 'Payment Processing', value: 'payments', hours: 180 },
  { label: 'Notifications & Alerts', value: 'notifications', hours: 90 },
  { label: 'Data Import/Export', value: 'data', hours: 110 },
  { label: 'Audit Logs', value: 'audit', hours: 110 },
  { label: 'Advanced Search Functionality', value: 'search', hours: 100 },
  { label: 'File Uploads & Storage', value: 'files', hours: 130 },
  { label: 'Real-time Updates', value: 'realtime', hours: 150 },
  { label: 'Multi-tenant / White-label', value: 'multiTenant', hours: 180 },
  { label: 'Mobile / PWA', value: 'mobile', hours: 200 },
  { label: 'Third-party Integrations', value: 'integrations', hours: 160 },
  { label: 'AI Features', value: 'ai', hours: 500 }
];

// Timeline options
const TIMELINE_OPTIONS = [
  { label: '1-2 months (Rush)', value: 'urgent' },
  { label: '3-4 months (Standard)', value: 'standard' },
  { label: '5-6 months (Standard)', value: 'standard-extended' },
  { label: '7+ months (Flexible)', value: 'flexible' },
];

// Budget ranges for reference
const BUDGET_RANGES = [
  '£5K - £10K',
  '£10K - £25K', 
  '£25K - £50K',
  '£50K - £100K',
  '£100K - £250K',
  '£250K+'
];

// Project keywords for detection
const PROJECT_KEYWORDS = [
  { type: 'crm', keywords: ['crm', 'sales', 'leads', 'pipeline', 'customer management'], baseHours: 520 },
  { type: 'erp', keywords: ['erp', 'inventory', 'finance', 'accounts', 'procurement', 'supply chain'], baseHours: 900 },
  { type: 'dashboard', keywords: ['dashboard', 'analytics', 'reporting', 'bi', 'kpi'], baseHours: 360 },
  { type: 'hr', keywords: ['hr', 'payroll', 'employees', 'attendance', 'recruitment'], baseHours: 600 },
  { type: 'project-management', keywords: ['project', 'task', 'kanban', 'timeline', 'gantt'], baseHours: 520 },
  { type: 'ecommerce', keywords: ['ecommerce', 'shop', 'store', 'cart', 'checkout', 'product catalog'], baseHours: 720 },
  { type: 'booking', keywords: ['booking', 'appointments', 'scheduling', 'reservations'], baseHours: 480 },
  { type: 'support', keywords: ['support', 'helpdesk', 'ticketing', 'customer support'], baseHours: 440 },
  { type: 'lms', keywords: ['lms', 'learning', 'courses', 'education', 'training'], baseHours: 680 },
  { type: 'cms', keywords: ['cms', 'content management', 'blog', 'publishing'], baseHours: 420 },
  { type: 'finance', keywords: ['finance', 'accounting', 'billing', 'invoicing', 'expenses'], baseHours: 640 },
  { type: 'analytics', keywords: ['analytics', 'tracking', 'metrics', 'data visualization'], baseHours: 520 },
  { type: 'iot', keywords: ['iot', 'sensors', 'devices', 'monitoring'], baseHours: 820 },
  { type: 'logistics', keywords: ['logistics', 'fleet', 'shipping', 'delivery', 'warehouse'], baseHours: 760 },
  { type: 'healthcare', keywords: ['healthcare', 'clinic', 'hospital', 'patient', 'medical'], baseHours: 900 },
  { type: 'fintech', keywords: ['fintech', 'banking', 'payments', 'wallet', 'transactions'], baseHours: 980 },
  { type: 'real-estate', keywords: ['real estate', 'property', 'rentals', 'listings'], baseHours: 620 },
  { type: 'marketing', keywords: ['marketing', 'campaigns', 'email marketing', 'seo'], baseHours: 480 },
  { type: 'email', keywords: ['email', 'newsletter', 'mailing list', 'email marketing', 'imap', 'smtp'], baseHours: 300 },
  { type: 'mail-client', keywords: ['mail client', 'email client', 'webmail'], baseHours: 400 },
  { type: 'internal', keywords: ['internal', 'workflow', 'automation', 'process'], baseHours: 520 },
  { type: 'portfolio', keywords: ['portfolio', 'showcase', 'personal website', 'profile'], baseHours: 80 },
  { type: 'wordpress', keywords: ['wordpress', 'wp', 'wordpress site'], baseHours: 60 },
  { type: 'landing-page', keywords: ['landing page', 'landing', 'promotional page', 'squeeze page'], baseHours: 40 },
  { type: 'corporate-website', keywords: ['corporate website', 'company website', 'business website'], baseHours: 90 },
  { type: 'blog', keywords: ['blog', 'blogging platform', 'news site'], baseHours: 70 },
  { type: 'directory', keywords: ['directory', 'listing site', 'business directory'], baseHours: 480 },
  { type: 'marketplace', keywords: ['marketplace', 'multi-vendor', 'peer-to-peer'], baseHours: 880 },
  { type: 'membership', keywords: ['membership', 'subscription site', 'members area'], baseHours: 320 },
  { type: 'forum', keywords: ['forum', 'community', 'discussion board'], baseHours: 440 },
  { type: 'stock', keywords: ['stock', 'stock management', 'stock transfers', 'inventory tracking', 'stock control', 'trading'], baseHours: 580 },
];

const HOURLY_RATE = 27; // £40/hour
const postQuote = async ({
  userName,
  userEmail,
  projectIdea,
  numberOfUsers,
  timeline,
  selectedFeatures,
  estimatedMin,
  estimatedMax,
  estimatedHours,
  quoteNumber,
  validUntil
}) => {
  const response = await fetch('https://demo.lolcdn.xyz/quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userName,
      userEmail,
      projectIdea,
      numberOfUsers,
      timeline,
      selectedFeatures,
      estimatedMin,
      estimatedMax,
      estimatedHours,
      quoteNumber,
      validUntil
    })
  });

  if (!response.ok) {
    throw new Error('Failed to submit quote');
  }

  return response.json();
};



export const InstantQuotePage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [searchParams] = useSearchParams();

  // Form state
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    projectIdea: '',
    numberOfUsers: '',
    timeline: ''
  });
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  
  // Quote state
  const [generating, setGenerating] = useState(false);
  const [quoteGenerated, setQuoteGenerated] = useState(false);
  const [estimatedMin, setEstimatedMin] = useState(0);
  const [estimatedMax, setEstimatedMax] = useState(0);
  const [estimatedHours, setEstimatedHours] = useState(0);
  const [quoteNumber, setQuoteNumber] = useState('');
  const [validUntil, setValidUntil] = useState('');

  // Pre-fill project idea from URL params
  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam) {
      setFormData(prev => ({ ...prev, projectIdea: projectParam }));
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (featureValue) => {
    setSelectedFeatures(prev => 
      prev.includes(featureValue)
        ? prev.filter(f => f !== featureValue)
        : [...prev, featureValue]
    );
  };

  const generateQuote = async () => {
    if (!formData.projectIdea || !formData.numberOfUsers || !formData.timeline) {
      return;
    }

    setGenerating(true);
    setQuoteGenerated(true);

    // Simulate computation delay (3-8 seconds)
    const delay = Math.floor(Math.random() * 5000) + 3000;
    await new Promise(resolve => setTimeout(resolve, delay));

    const lower = formData.projectIdea.toLowerCase();
    const matches = PROJECT_KEYWORDS.filter(p =>
      p.keywords.some(k => lower.includes(k))
    );

    // Base hours for multiple matches (primary full, others discounted)
    let hours =
      matches.length > 0
        ? matches.reduce((sum, p, idx) => sum + (idx === 0 ? p.baseHours : p.baseHours * 0.6), 0)
        : 180;

    // Add hours based on number of users
    hours += parseInt(formData.numberOfUsers) * 1.5;

    // Add hours for selected features
    selectedFeatures.forEach(featureValue => {
      const feature = FEATURES.find(f => f.value === featureValue);
      if (feature) {
        hours += feature.hours;
      }
    });

    // Adjust based on timeline
    if (formData.timeline === 'standard' || formData.timeline === 'standard-extended') {
      hours *= 1.2;
    } else if (formData.timeline === 'urgent') {
      hours *= 1.5;
    }

    // Round to nearest 10
    hours = Math.round(hours / 10) * 10;

    // Calculate price with a range (±15%)
    const basePrice = Math.round(hours * HOURLY_RATE);
    const calculatedMin = Math.round(basePrice * 0.85);
    const calculatedMax = Math.round(basePrice * 1.15);

    setEstimatedMin(calculatedMin);
    setEstimatedMax(calculatedMax);
    setEstimatedHours(hours);

    const now = new Date();
    const valid = new Date(now);
    valid.setDate(valid.getDate() + 30);

    const generatedQuoteNumber = `LOL-${Array.from({ length: 6 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]).join('')}`;
    const generatedValidUntil = valid.toLocaleDateString('en-GB');

    setQuoteNumber(generatedQuoteNumber);
    setValidUntil(generatedValidUntil);

    setGenerating(false);

    try {
      await postQuote({
        userName: formData.userName,
        userEmail: formData.userEmail,
        projectIdea: formData.projectIdea,
        numberOfUsers: formData.numberOfUsers,
        timeline: formData.timeline,
        selectedFeatures,
        estimatedMin: calculatedMin,
        estimatedMax: calculatedMax,
        estimatedHours: hours,
        quoteNumber: generatedQuoteNumber,
        validUntil: generatedValidUntil
      });
    } catch (error) {
      console.error(error);
    }
  };

  const isFormValid = formData.projectIdea && formData.numberOfUsers && formData.timeline;
  const estimatedTimelineMonths = estimatedHours
    ? Math.ceil(estimatedHours / (40 * 4))
    : 0;


  return (
    <div className="min-h-screen bg-white" data-testid="instant-quote-page">
      <Navigation />
      
      {/* Hero Section */}
         <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#003333]/60 hover:text-[#003333] mb-8 transition-colors"
          data-testid="back-to-home"
            >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
            </Link>
            
            <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-left max-w-4xl"
            >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            Generate a Quote
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.9] text-[#003333] mb-6">
            Get an Instant Quote for Custom Software
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-[#003333]/70 max-w-3xl ">
            Replace spreadsheets, emails, and fragile workflows with a secure, scalable internal system built
            around how your business actually works. Use our automated quote generator to receive a real-time 
            estimate based on your project idea, features, timeline, and user requirements.
          </p>
          <p className="text-[#003333]/50 mt-4">
            No sales calls. No hidden fees. Just a realistic ballpark quote, instantly.
          </p>
            </motion.div>
          </div>
        </section>

        {/* Quote Form Section */}
      <section className="py-16 md:py-24 bg-white" ref={ref}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.8 }}
            className="bg-[#F8FAFC] p-8 md:p-12 rounded-xl"
          >
            {/* Form Header */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#003333] mb-2">
                Instant Quote Generator
              </h2>
              <p className="text-slate-600">
                Fill out the form below to get your instant quote.
              </p>
            </div>

            {/* Quote Form */}
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()} data-testid="quote-form">
              {/* Name and Email Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    name="userName"
                    placeholder="Enter your name"
                    value={formData.userName}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all"
                    data-testid="quote-name-input"
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    name="userEmail"
                    placeholder="Enter your email"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all"
                    data-testid="quote-email-input"
                  />
                </div>
              </div>

              {/* Project Idea */}
              <div className="relative">
                <div className="absolute left-4 top-4 text-slate-400">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <textarea
                  name="projectIdea"
                  placeholder="Describe your project in one sentence"
                  value={formData.projectIdea}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all resize-none"
                  data-testid="quote-project-input"
                />
              </div>

              {/* Number of Users and Timeline Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                    <Users className="w-5 h-5" />
                  </div>
                  <input
                    type="number"
                    name="numberOfUsers"
                    placeholder="Number of users"
                    min="1"
                    value={formData.numberOfUsers}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg text-[#003333] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all"
                    data-testid="quote-users-input"
                  />
                </div>
                
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg text-[#003333] focus:outline-none focus:ring-2 focus:ring-[#003333]/20 focus:border-[#003333] transition-all appearance-none cursor-pointer"
                    data-testid="quote-timeline-select"
                  >
                    <option value="" disabled>Select timeline</option>
                    {TIMELINE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Features Selection */}
              <div>
                <label className="block text-[#003333] font-medium mb-4">
                  Select Required Features (Optional):
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-y-auto p-1">
                  {FEATURES.map((feature) => (
                    <label
                      key={feature.value}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedFeatures.includes(feature.value)
                          ? 'bg-[#003333] text-white'
                          : 'bg-white border border-slate-200 text-[#003333] hover:border-[#003333]/30'
                      }`}
                      data-testid={`feature-${feature.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFeatures.includes(feature.value)}
                        onChange={() => handleFeatureToggle(feature.value)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selectedFeatures.includes(feature.value)
                          ? 'bg-white border-white'
                          : 'border-slate-300'
                      }`}>
                        {selectedFeatures.includes(feature.value) && (
                          <CheckCircle className="w-4 h-4 text-[#003333]" />
                        )}
                      </div>
                      <span className="text-sm">{feature.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Disclaimers */}
              <div className="bg-white/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-3 text-slate-600">
                  <Shield className="w-5 h-5 text-[#003333]" />
                  <span className="text-sm">No obligation, no pressure</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Zap className="w-5 h-5 text-[#003333]" />
                  <span className="text-sm">100% free automated quote</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Calculator className="w-5 h-5 text-[#003333]" />
                  <span className="text-sm">Enter details to see your price instantly</span>
                </div>
              </div>

              {/* Generate Button */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={generateQuote}
                  disabled={!isFormValid || generating}
                  className="inline-flex items-center gap-2 bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  data-testid="generate-quote-button"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    'Generate Instant Quote'
                  )}
                </button>
              </div>

              {/* Quote Result */}
              {quoteGenerated && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8"
                  data-testid="quote-result"
                >
                  {generating ? (
                    <div className="bg-white rounded-xl p-8 flex flex-col items-center justify-center min-h-[200px]">
                      <Loader2 className="w-12 h-12 text-[#003333] animate-spin mb-4" />
                      <p className="text-slate-600">Analyzing your project requirements...</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl p-8 border border-[#003333]/10">
                      <h3 className="text-xl font-semibold text-[#003333] mb-4">
                        Your Automated Quote
                      </h3>
                      
                      <div className="space-y-4">
                        <p className="text-slate-700">
                          <strong>{formData.projectIdea}</strong> for{' '}
                          <strong>{formData.numberOfUsers}</strong>{' '}
                          {parseInt(formData.numberOfUsers) === 1 ? 'user' : 'users'}
                        </p>

                        <div className="grid sm:grid-cols-3 gap-4 text-sm text-slate-600">
                          <div>
                            <p className="text-slate-400">Quote #</p>
                            <p className="font-medium text-[#003333]">{quoteNumber}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Valid Until</p>
                            <p className="font-medium text-[#003333]">{validUntil}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Estimated Timeline</p>
                            <p className="font-medium text-[#003333]">
                              {estimatedTimelineMonths} {estimatedTimelineMonths === 1 ? 'month' : 'months'} (40 hrs/week)
                            </p>
                          </div>
                        </div>
                        
                        <div className="py-4 border-t border-b border-slate-100">
                          <p className="text-sm text-slate-500 mb-2">Estimated Project Cost:</p>
                          <p className="text-3xl md:text-4xl font-bold text-[#003333]">
                            £{estimatedMin.toLocaleString()} - £{estimatedMax.toLocaleString()}
                          </p>
                        </div>
                        
                        <p className="text-xs text-slate-400">
                          This is a preliminary estimate based on the information provided. 
                          Actual cost may vary based on detailed requirements.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mt-6">
                          <Link
                            to="/consultation"
                            className="inline-flex items-center justify-center gap-2 bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-6 py-3 font-medium transition-all duration-300 hover:scale-105"
                            data-testid="book-consultation-from-quote"
                          >
                            Book a Consultation
                          </Link>
                          <a
                            href="/#contact"
                            className="inline-flex items-center justify-center gap-2 border border-[#003333] text-[#003333] hover:bg-[#003333] hover:text-white rounded-full px-6 py-3 font-medium transition-all duration-300"
                            data-testid="contact-from-quote"
                          >
                            Contact Us
                          </a>
                        </div>
                      </div>


                      <div className="mt-6 rounded-lg border border-slate-100 bg-slate-50 p-4">
                        <h4 className="text-sm font-semibold text-[#003333] mb-2">Basic Terms</h4>
                        <ul className="list-disc pl-5 text-xs text-slate-500 space-y-1">
                          <li>Typical engagement starts with a 30% (£{(estimatedMax * 0.3).toLocaleString('en-GB')}) upfront payment to commence work.</li>
                          <li>Remaining balance is billed across agreed milestones or monthly.</li>
                          <li>Final delivery occurs after approval and final payment.</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default InstantQuotePage;
